import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { Clock, AlertTriangle, AlertCircle } from "lucide-react";
import { useSession } from "@/hooks/useSession";
import { cn } from "@/lib/utils";

// Timer threshold constants
const TIMER_THRESHOLDS = {
  CRITICAL_SECONDS: 120, // 2 minutes
  WARNING_SECONDS: 300,  // 5 minutes
} as const;

interface HoldTimerProps {
  /** ISO timestamp when the hold expires */
  expiresAt: string;
  /** Callback when timer expires (before redirect) */
  onExpire?: () => void;
  /** Optional className for container */
  className?: string;
}

type TimerState = "normal" | "warning" | "critical";

/**
 * Displays a countdown timer for seat holds.
 * Visual states: normal (>5min), warning (2-5min), critical (<2min)
 * Redirects to search home when expired.
 */
export function HoldTimer({ expiresAt, onExpire, className }: HoldTimerProps) {
  const { t } = useTranslation();
  const { clearSession } = useSession();
  const [, setLocation] = useLocation();
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [timerState, setTimerState] = useState<TimerState>("normal");
  const [isExpired, setIsExpired] = useState(false);
  const isExpiredRef = useRef(false);

  // Calculate time remaining from expiration timestamp
  const calculateTimeRemaining = useCallback(() => {
    const now = Date.now();
    const expiry = new Date(expiresAt).getTime();

    // Handle invalid timestamp
    if (isNaN(expiry)) {
      console.error(`Invalid expiresAt timestamp: ${expiresAt}`);
      return 0;
    }

    return Math.max(0, Math.floor((expiry - now) / 1000));
  }, [expiresAt]);

  // Update timer state based on remaining time
  const updateTimerState = useCallback((seconds: number) => {
    if (seconds <= TIMER_THRESHOLDS.CRITICAL_SECONDS) {
      setTimerState("critical");
    } else if (seconds <= TIMER_THRESHOLDS.WARNING_SECONDS) {
      setTimerState("warning");
    } else {
      setTimerState("normal");
    }
  }, []);

  // Initialize and update timer
  useEffect(() => {
    // Already expired - don't run effect
    if (isExpiredRef.current) return;

    // Initial calculation
    const initial = calculateTimeRemaining();
    setTimeRemaining(initial);
    updateTimerState(initial);

    // Check if already expired on mount
    if (initial <= 0) {
      isExpiredRef.current = true;
      setIsExpired(true);
      onExpire?.();
      clearSession();
      setTimeout(() => setLocation(`/`), 2000);
      return;
    }

    // Update every second
    const interval = setInterval(() => {
      if (isExpiredRef.current) {
        clearInterval(interval);
        return;
      }

      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);
      updateTimerState(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
        isExpiredRef.current = true;
        setIsExpired(true);
        onExpire?.();
        clearSession();
        setTimeout(() => setLocation(`/`), 2000);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [calculateTimeRemaining, updateTimerState, onExpire, clearSession, setLocation]);

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Get styles based on timer state
  const getStateStyles = () => {
    switch (timerState) {
      case "critical":
        return "bg-destructive/10 text-destructive border-destructive/30";
      case "warning":
        return "bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/30";
      default:
        return "bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30";
    }
  };

  // Get icon based on timer state for accessibility (non-color indicator)
  const getStateIcon = () => {
    switch (timerState) {
      case "critical":
        return <AlertTriangle className="h-4 w-4" />;
      case "warning":
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  // Expired state
  if (isExpired) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 px-4 py-3 rounded-lg border",
          "bg-destructive/10 text-destructive border-destructive/30",
          className
        )}
        role="alert"
        aria-live="assertive"
      >
        <AlertTriangle className="h-4 w-4" />
        <span className="font-medium">{t("holdTimer.expired")}</span>
        <span className="text-sm">{t("holdTimer.expiredMessage")}</span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center gap-2 px-4 py-3 rounded-lg border transition-colors",
        getStateStyles(),
        timerState === "critical" && "animate-pulse",
        className
      )}
      role="timer"
      aria-atomic="true"
    >
      {getStateIcon()}
      <span className="font-medium" aria-label={`${formatTime(timeRemaining)} remaining`}>
        {formatTime(timeRemaining)}
      </span>
      <span className="text-sm opacity-80">
        {timerState === "critical"
          ? t("holdTimer.critical")
          : timerState === "warning"
          ? t("holdTimer.warning")
          : t("holdTimer.timeRemaining")}
      </span>
    </div>
  );
}
